// ruleFieldValues.js — build the { ruleField: [values] } map that RuleBuilder
// uses to populate its value dropdowns, from app config (+ optional TherapieMaps
// for region/clinic). So rule authors PICK real values instead of guessing.

function uniq(arr) {
  return [...new Set((arr || []).filter(Boolean))];
}

export function buildRuleFieldValues(config = {}, tmData = null) {
  const vals = (rows) => uniq((rows || []).map(r => r?.value).filter(Boolean));
  const subtypes = (config.treatmentTypes || []).flatMap(t => (Array.isArray(t.subtypes) ? t.subtypes : []));

  const fv = {
    treatment:        vals(config.treatmentTypes),
    treatmentSubtype: uniq(subtypes),
    severity:         vals(config.severityCodes),
    status:           vals(config.statusValues),
    eventType:        vals(config.eventTypes),
  };

  // incidentCategory — flatten the unique category strings from the cascade tree.
  const tree = config.incidentCategoriesByTreatmentAndCode;
  if (tree && typeof tree === 'object') {
    const cats = new Set();
    const walk = (node) => {
      if (Array.isArray(node)) node.forEach(c => c && cats.add(c));
      else if (node && typeof node === 'object') Object.values(node).forEach(walk);
    };
    walk(tree);
    if (cats.size) fv.incidentCategory = [...cats].sort();
  }

  // region / clinic / country — from the TherapieMaps clinic directory when loaded.
  const clinics = tmData?.clinics;
  if (Array.isArray(clinics) && clinics.length) {
    fv.clinic  = uniq(clinics.map(c => c.name)).sort();
    fv.region  = uniq(clinics.map(c => c.region)).sort();
    fv.country = uniq(clinics.map(c => c.country)).sort();
  }

  // requirement* — from the admin requirementTypes vocabulary ({ key, label }
  // rows). requirementKey also always offers the two dispatch fallback keys:
  // 'other' (free-text document ask) and 'signoff' (a completed sign-off).
  const reqTypes = (config.requirementTypes || []).filter(t => t && typeof t === 'object');
  fv.requirementKey   = uniq([...reqTypes.map(t => t.key), 'other', 'signoff']);
  fv.requirementLabel = uniq(reqTypes.map(t => t.label));

  // Drop empty arrays so RuleBuilder falls back to free text for those fields.
  for (const k of Object.keys(fv)) if (!fv[k] || !fv[k].length) delete fv[k];
  return fv;
}
